import { useEffect, useState, useCallback } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  User,
  Utensils,
  Award,
  CalendarDays,
  MapPin,
  LogOut,
  Menu as MenuIcon,
  ChevronLeft,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BrandMark } from "@/components/BrandMark";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/location", label: "Location", icon: MapPin, testid: "nav-location" },
  { to: "/app/menu", label: "Menu", icon: Utensils, testid: "nav-menu" },
  { to: "/app/events", label: "Events", icon: CalendarDays, testid: "nav-events" },
  { to: "/app/awards", label: "Awards", icon: Award, testid: "nav-awards" },
  { to: "/app/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

export default function DashboardLayout() {
  const { user, vendor, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [currentLoc, setCurrentLoc] = useState(null);

  const refreshLive = useCallback(async () => {
    try {
      const { data } = await api.get("/vendors/me/current-location");
      setCurrentLoc(data);
    } catch (_e) {
      setCurrentLoc(null);
    }
  }, []);

  useEffect(() => {
    refreshLive();
    const t = setInterval(refreshLive, 30000);
    return () => clearInterval(t);
  }, [refreshLive]);

  // Re-fetch when window regains focus (e.g., after going live on Location page)
  useEffect(() => {
    const onFocus = () => refreshLive();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshLive]);

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/login", { replace: true });
  };

  const isLive = !!(vendor?.is_live && currentLoc);
  const liveLabel = currentLoc?.name || currentLoc?.address || "Live now";

  const SidebarContent = ({ showLabels = true }) => (
    <>
      <Link to="/app/dashboard" className="flex items-center gap-2 px-3 py-4" data-testid="sidebar-brand">
        <BrandMark size="sm" asLink={false} testid="sidebar-brand-mark" />
        {showLabels && <span className="font-bold tracking-tight text-lg">FoodieFinder</span>}
      </Link>

      {/* Live status indicator */}
      <div
        className={`mx-3 mb-3 rounded-lg border px-3 py-2 text-xs ${
          isLive ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"
        }`}
        data-testid="sidebar-live-indicator"
      >
        {showLabels ? (
          <div className="flex items-start gap-2">
            {isLive ? <span className="ff-live-dot-sm mt-1 flex-shrink-0" /> : <span className="mt-1 inline-block h-2 w-2 rounded-full bg-slate-400 flex-shrink-0" />}
            <div className="min-w-0">
              <div className={`font-semibold ${isLive ? "text-emerald-700" : "text-slate-600"}`}>
                {isLive ? "Live now" : "Offline"}
              </div>
              {isLive && <div className="text-emerald-700/80 truncate" data-testid="sidebar-live-location">{liveLabel}</div>}
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            {isLive ? <span className="ff-live-dot-sm" /> : <span className="inline-block h-2 w-2 rounded-full bg-slate-400" />}
          </div>
        )}
      </div>

      <nav className="px-2 space-y-1">
        {NAV.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => `ff-nav-link ${isActive ? "active" : ""} ${showLabels ? "" : "justify-center"}`}
            data-testid={testid}
            title={!showLabels ? label : undefined}
          >
            <Icon size={18} className="flex-shrink-0" />
            {showLabels && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto px-2 pb-4">
        {showLabels && (
          <div className="px-3 py-2 text-xs text-slate-500 truncate" data-testid="sidebar-user-email">
            {user?.email}
          </div>
        )}
        <button
          onClick={handleLogout}
          className={`ff-nav-link w-full ${showLabels ? "" : "justify-center"}`}
          data-testid="sidebar-logout"
          title={!showLabels ? "Sign out" : undefined}
        >
          <LogOut size={18} className="flex-shrink-0" />
          {showLabels && <span>Sign out</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-white flex">
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-white border-r border-slate-200 transition-all duration-200 ${
          collapsed ? "w-20" : "w-64"
        }`}
        data-testid="desktop-sidebar"
      >
        <SidebarContent showLabels={!collapsed} />
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="absolute top-4 -right-3 hidden lg:grid place-items-center h-6 w-6 rounded-full bg-white border border-slate-200 shadow-sm hover:bg-slate-50"
          style={{ left: collapsed ? "4.25rem" : "15.25rem" }}
          data-testid="sidebar-collapse-toggle"
          aria-label="Toggle sidebar"
        >
          {collapsed ? <MenuIcon size={12} /> : <ChevronLeft size={12} />}
        </button>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden bg-white border-b border-slate-200 px-4 h-14 flex items-center justify-between sticky top-0 z-20">
          <Link to="/app/dashboard" className="flex items-center gap-2" data-testid="mobile-brand">
            <BrandMark size="xs" asLink={false} testid="mobile-brand-mark" />
            <span className="font-bold tracking-tight">FoodieFinder</span>
          </Link>
          <div className="flex items-center gap-2">
            {isLive ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-testid="mobile-live-indicator">
                <span className="ff-live-dot-sm" /> Live
              </span>
            ) : (
              <span className="text-xs text-slate-500" data-testid="mobile-offline-indicator">Offline</span>
            )}
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="mobile-logout">
              <LogOut size={16} />
            </Button>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 pb-24 lg:pb-8 overflow-x-hidden">
          <Outlet />
        </main>

        {/* Mobile bottom nav — icons only */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-30" data-testid="mobile-bottom-nav">
          <div className="grid grid-cols-6">
            {NAV.map(({ to, icon: Icon, testid, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `flex items-center justify-center h-14 ${isActive ? "text-[#E8650A]" : "text-slate-500"}`
                }
                data-testid={`${testid}-mobile`}
                aria-label={label}
              >
                <Icon size={22} />
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
}
